import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { useMobileTrpc } from 'shared/api'
import { PublicOfferDialog } from 'shared/components/PublicOfferDialog'
import { Button } from 'shared/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from 'shared/components/ui/dialog'
import { Input } from 'shared/components/ui/input'
import { isValidTezId } from 'shared/lib/tezid'
import { useTranslate } from 'shared/lib/i18n'
import { useAuthStore } from 'shared/store/auth'

type SaveResult =
	| { type: 'success'; bonusGranted: boolean }
	| { type: 'error'; message: string }

export function TezIdDialog({
	open,
	onOpenChange,
	initialValue = '',
	onSuccess,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	initialValue?: string
	onSuccess?: () => void
}) {
	const trpc = useMobileTrpc()
	const setUser = useAuthStore((s) => s.setUser)
	const { t } = useTranslate()
	const [value, setValue] = useState(initialValue)
	const [showOffer, setShowOffer] = useState(() => !initialValue.trim())
	const [publicOfferAccepted, setPublicOfferAccepted] = useState(false)
	const [saveResult, setSaveResult] = useState<SaveResult | null>(null)

	const mutation = useMutation(trpc.profile.SetTezId.mutationOptions())
	const valid = isValidTezId(value)

	async function handleSave() {
		try {
			const result = await mutation.mutateAsync({
				tezId: value,
				publicOfferAccepted,
			})
			setUser(result.user)
			setSaveResult({ type: 'success', bonusGranted: result.bonusGranted })
		} catch {
			setSaveResult({
				type: 'error',
				message: t('tezId.saveFailed'),
			})
		}
	}

	function closeFlow() {
		onOpenChange(false)
	}

	function finishResult() {
		const wasSuccessful = saveResult?.type === 'success'
		closeFlow()
		if (wasSuccessful) onSuccess?.()
	}

	return (
		<>
			<PublicOfferDialog
				open={open && showOffer}
				onOpenChange={(nextOpen) => {
					if (!nextOpen) closeFlow()
				}}
				onAccept={() => {
					setPublicOfferAccepted(true)
					setShowOffer(false)
				}}
			/>

			<Dialog
				open={open && !showOffer}
				onOpenChange={(nextOpen) => {
					if (!nextOpen) finishResult()
				}}
			>
				<DialogContent showCloseButton={!saveResult}>
					<DialogHeader>
						<DialogTitle>
							{saveResult?.type === 'success'
								? saveResult.bonusGranted
									? t('tezId.bonusGranted')
									: t('tezId.saved')
								: saveResult?.type === 'error'
									? t('tezId.saveFailedTitle')
									: t('tezId.yourTezId')}
						</DialogTitle>
						<DialogDescription>
							{saveResult?.type === 'success'
								? saveResult.bonusGranted
									? t('tezId.bonusGrantedDescription')
									: t('tezId.savedDescription')
								: saveResult?.type === 'error'
									? saveResult.message
									: t('tezId.description')}
						</DialogDescription>
					</DialogHeader>
					{!saveResult && (
						<div className='space-y-4 py-2'>
							<Input
								placeholder='TOS101'
								value={value}
								aria-invalid={Boolean(value && !valid)}
								onChange={(e) => setValue(e.target.value.toUpperCase())}
								className='h-10 text-base'
							/>
						</div>
					)}
					<DialogFooter>
						<Button
							className='h-10 w-full text-base font-semibold'
							disabled={!saveResult && (!valid || mutation.isPending)}
							onClick={saveResult ? finishResult : handleSave}
						>
							{saveResult
								? t('common.done')
								: mutation.isPending
									? t('common.saving')
									: t('common.save')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	)
}
